import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Close from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Container, IconButton, LinearProgress, Modal, Stack, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { AppContext } from '../../Base';
import CoinIcon from '../icons/Coin';

export default function RewardCard({ uid, rid, imageUrl, title, description, coins, isClickable, isClaimable, isAlreadyClaimed, setClaimMade }) {
    const [open, setOpen] = useState(false);
    const actualCoins = useContext(AppContext).coinsState; 

    const [isSubmitting, setSubmitting] = useState(false); 
    const [claimed, setClaimed] = useState(false);

    const handleOpen = () => {
        setOpen(true);
        setClaimMade(false);
    }
    const handleClose = () => {
        setOpen(false);
        if (claimed) setClaimMade(true);
    }
    const handleClaim = () => {
        setClaimed(true);
        setSubmitting(true);

        const url = process.env.REACT_APP_REWARD_LAMBDA;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify({
                "httpMethod": "POST", // For routing
                "resourcePath": "/rewards", // For routing
                'rid': rid,
                'uid': uid,
                'rewardTitle': title,
                'coins': coins,
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            let results = JSON.parse(data?.body || '{}');
            // console.log(results)
            
            // Set display flags
            setSubmitting(false);
            setClaimed(results?.isSuccess);

        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        })
    };

    return (
        <>
        <Box 
            className="rewardcard-container" 
            onClick={isClickable ? handleOpen : ()=>{}}
            sx={{
                filter: isAlreadyClaimed ? 'grayscale(100%)' : 'none', 
                backgroundColor: isAlreadyClaimed ? 'var(--pri-grey)' : 'var(--pri-purple)',
                cursor: 'pointer',
                ...((isAlreadyClaimed || !isClickable) ? {} : {
                    transition: 'box-shadow 0.5s, transform 0.5s',
                    '&:hover' : {
                        transform: 'translate(-4px,-4px)',
                        boxShadow: '4px 4px var(--dark-purple)',
                    }
                })
        }}>
            <Box alt={title} sx={{ width: '40%',  aspectRatio: 1, background: `var(--pri-purple) url(${imageUrl}) no-repeat center`, backgroundSize: 'cover' }} />

            {/* Right Content */}
            <Box className="rewardcard-text-container spread-y">
                <Box className="spread-x" sx={{ alignItems: 'start' }}>
                    <Typography variant="h6">
                        {title}
                    </Typography>
                    {isClickable && (
                        <IconButton>
                            <ArrowForwardIcon sx={{ color: 'white' }} />
                        </IconButton>
                    )}
                </Box>
                <Typography variant="caption" sx={{ marginTop: 1 }}>
                    {description}
                </Typography>
                <Box className="spread-x" sx={{ justifyContent: 'flex-start', gap: 1 }}>
                    {isClickable && (
                    <Box sx={{ width: '70%' }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min((actualCoins / coins), 1) * 100}
                            className='reward-progress-bar'/>
                    </Box>
                    )}
                    <Typography variant="body2">
                        {coins}
                    </Typography>
                    <CoinIcon />
                </Box>
            </Box>
        </Box>

        <Modal 
            open={open} 
            onClose={handleClose}
        >
            <Container maxWidth={'xs'} className='modal-container' sx={{ padding: 3 }}>
                {/* Close Button */}
                <Box className="cross-container" >
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Box>

                <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center' }}>

                    { isSubmitting ?
                        <Box className='centered-container' sx={{ minHeight: '40vh' }}>
                            <CircularProgress color='secondary' />
                        </Box>
                    : (
                    <>
                        <img
                            src={imageUrl}
                            alt={title}
                            style={{ transition: 'width 0.3s ease-out', width: claimed ? '40%' : '70%', borderRadius: '8px' }}
                        />
                        { !claimed ? (
                            <>
                                <Typography variant="h6">
                                    {title}
                                </Typography>
                                <Typography variant="body2">
                                    {
                                        actualCoins < coins ?
                                        <>You need {coins} coins to claim a {title}</>
                                        :
                                        <>You have enough coins to claim a {title}!</>
                                    }
                                    
                                </Typography>

                                <Box className="spread-x" sx={{ width: '100%' }}>
                                    <Box sx={{ flex: 'auto', pr: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min((actualCoins / coins), 1) * 100}
                                        className='reward-progress-bar'
                                    />
                                    </Box>
                                    <Typography variant="body2">
                                        {Math.min(actualCoins, coins)}/{coins}&ensp;
                                    </Typography>
                                    <CoinIcon />
                                </Box>

                                <Button 
                                    className={`pill_btn ${(!isClaimable) ? 'Mui-disabled' : 'pri_btn'}`} 
                                    sx={{ mt: 3, width: '100%' }} 
                                    onClick={handleClaim}
                                    disabled={actualCoins < coins}
                                >
                                    Claim Reward
                                </Button>
                            </>
                        ) : (
                            <>
                                <Typography variant="h6">
                                    Reward Claimed!
                                </Typography>
                                <Typography variant="body2">
                                    You have successfully claimed a <br /><b>{title}</b>.
                                </Typography>
                                <Typography variant="body2" fontSize={12}>
                                    Keep an eye on your email inbox for a message from our iCare team with the collection date and time for picking up your reward(s).
                                    <br />
                                    <br />
                                    If you have any questions, feel free to reach out to us at <a href={`mailto:${process.env.REACT_APP_HELP_EMAIL}`}>{process.env.REACT_APP_HELP_EMAIL}</a>
                                </Typography>
                            </>
                        )}
                    </>
                    )}
                </Stack>
            </Container>
        </Modal>
        </>
    );
}
